import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { API, authFetch } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import PortalLayout from "../components/PortalLayout";
import MyServices from "../components/MyServices";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  Calculator,
  CreditCard,
  CheckCircle,
  FileText,
  BookOpen,
  CalendarRange,
  Users,
  Tag,
  Percent,
} from "lucide-react";

const SERVICE_OPTIONS = [
  { id: "itr_filing", title: "ITR Filing", desc: "Income Tax Return preparation and e-filing", icon: FileText, basePrice: "1,500" },
  { id: "accounting", title: "Cloud Accounting", desc: "Complete bookkeeping and financial management", icon: BookOpen, basePrice: "5,000" },
  { id: "both", title: "Combined Package", desc: "ITR Filing + Cloud Accounting services", icon: Calculator, basePrice: "6,000" },
];

const INCOME_SOURCES = [
  { id: "salary", label: "Salary Income" },
  { id: "business", label: "Business/Profession Income" },
  { id: "capital_gains", label: "Capital Gains" },
  { id: "rental", label: "Rental/House Property Income" },
  { id: "foreign", label: "Foreign Income/Assets" },
  { id: "agriculture", label: "Agriculture Income (exempt under Sec 10(1))" },
];

const PackageSelection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [mainTab, setMainTab] = useState("my_services");

  // Profiles
  const [profiles, setProfiles] = useState([]);
  const [profileSelections, setProfileSelections] = useState({});
  // Each: { selected: bool, service_type: string, questionnaire: {...}, fee: null }

  // Discount
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");

  // Consolidated fee
  const [totalFee, setTotalFee] = useState(null);

  // FY
  const fyOptions = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const startFY = currentMonth >= 3 ? currentYear : currentYear - 1;
    return Array.from({ length: 4 }, (_, i) => { const yr = startFY + i - 2; return `${yr}-${yr + 1}`; });
  }, []);

  const defaultFY = useMemo(() => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${y}-${y + 1}`;
  }, []);

  const selectedFY = searchParams.get("fy") || defaultFY;

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const res = await authFetch(`${API}/profiles`);
        if (res.ok) {
          const data = await res.json();
          setProfiles(data);
          // Init selections: if no profiles, create a "self" placeholder
          const sel = {};
          if (data.length === 0) {
            sel["__self__"] = { selected: true, service_type: "", questionnaire: defaultQuestionnaire(), fee: null };
          } else {
            data.forEach((p) => {
              sel[p.profile_id] = { selected: true, service_type: "", questionnaire: defaultQuestionnaire(), fee: null };
            });
          }
          setProfileSelections(sel);
        }
      } catch {}
    };
    fetchProfiles();
  }, []);

  function defaultQuestionnaire() {
    return { income_sources: [], annual_turnover: "", business_type: "", transaction_volume: "", gst_registered: false, has_audit_requirement: false, agriculture_income: "" };
  }

  const selectedProfiles = Object.entries(profileSelections).filter(([, v]) => v.selected && v.service_type);
  const hasMultipleProfiles = profiles.length > 1;

  const updateSelection = (pid, field, value) => {
    setProfileSelections((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: value } }));
  };

  const updateQuestionnaire = (pid, field, value) => {
    setProfileSelections((prev) => ({
      ...prev,
      [pid]: { ...prev[pid], questionnaire: { ...prev[pid].questionnaire, [field]: value } },
    }));
  };

  const toggleIncomeSource = (pid, sourceId) => {
    setProfileSelections((prev) => {
      const q = prev[pid].questionnaire;
      const sources = q.income_sources.includes(sourceId)
        ? q.income_sources.filter((s) => s !== sourceId)
        : [...q.income_sources, sourceId];
      return { ...prev, [pid]: { ...prev[pid], questionnaire: { ...q, income_sources: sources } } };
    });
  };

  const handleUpgrade = (payment) => {
    setMainTab("new_service");
    setStep(1);

    try {
      const q = typeof payment.questionnaire === 'string' ? JSON.parse(payment.questionnaire) : payment.questionnaire;
      
      setProfileSelections(prev => {
        const newSelections = { ...prev };
        // Unselect all
        Object.keys(newSelections).forEach(k => { newSelections[k].selected = false; });
        
        if (q && q.profile_items && q.profile_items.length > 0) {
          q.profile_items.forEach(item => {
            const pid = item.profile_id || "__self__";
            if (newSelections[pid]) {
              newSelections[pid].selected = true;
              newSelections[pid].service_type = item.service_type || payment.service_type;
              // We only have the first profile's questionnaire saved in DB, so we apply it to the first matching one
              if (q.income_sources || q.annual_turnover) {
                 newSelections[pid].questionnaire = { ...defaultQuestionnaire(), ...q };
                 // Delete the extra metadata
                 delete newSelections[pid].questionnaire.discount_code;
                 delete newSelections[pid].questionnaire.discount_amount;
                 delete newSelections[pid].questionnaire.profile_items;
              }
            }
          });
        } else {
          // Fallback
          const pid = Object.keys(newSelections)[0] || "__self__";
          if (newSelections[pid]) {
            newSelections[pid].selected = true;
            newSelections[pid].service_type = payment.service_type;
            if (q) {
               newSelections[pid].questionnaire = { ...defaultQuestionnaire(), ...q };
               delete newSelections[pid].questionnaire.discount_code;
               delete newSelections[pid].questionnaire.discount_amount;
               delete newSelections[pid].questionnaire.profile_items;
            }
          }
        }
        return newSelections;
      });
      toast.success("Loaded previous service details. You can now upgrade or add info.");
    } catch (e) {
      console.error(e);
      toast.error("Could not load previous details fully.");
    }
  };

  // Calculate fees for all selected profiles
  const calculateAllFees = async () => {
    setLoading(true);
    try {
      let grandTotal = 0;
      let grandBase = 0;
      let grandComplexity = 0;
      const updatedSelections = { ...profileSelections };
      const profileItems = [];

      for (const [pid, sel] of Object.entries(updatedSelections)) {
        if (!sel.selected || !sel.service_type) continue;
        const payload = { ...sel.questionnaire, service_type: sel.service_type };
        const res = await authFetch(`${API}/calculate-fee`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const feeData = await res.json();
          updatedSelections[pid] = { ...sel, fee: feeData };
          grandTotal += feeData.total_amount;
          grandBase += feeData.base_fee;
          grandComplexity += feeData.complexity_fee;
          const profName = pid === "__self__" ? (user?.name || "Self") : (profiles.find((p) => p.profile_id === pid)?.full_name || pid);
          profileItems.push({ profile_id: pid, profile_name: profName, service_type: sel.service_type, amount: feeData.total_amount });
        }
      }

      setProfileSelections(updatedSelections);

      // Apply discount if code entered
      let discountAmount = 0;
      let discountInfo = null;
      if (discountCode.trim()) {
        try {
          const dcRes = await fetch(`${API}/validate-discount`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code: discountCode.trim(), subtotal: grandTotal }),
            credentials: "include",
          });
          if (dcRes.ok) {
            const dcData = await dcRes.json();
            discountAmount = dcData.discount_amount;
            discountInfo = dcData;
            setAppliedDiscount(dcData);
            setDiscountError("");
          } else {
            const err = await dcRes.json();
            setDiscountError(err.detail || "Invalid code");
            setAppliedDiscount(null);
          }
        } catch {
          setDiscountError("Failed to validate discount code");
        }
      } else {
        setAppliedDiscount(null);
        setDiscountError("");
      }

      setTotalFee({
        base_fee: grandBase, complexity_fee: grandComplexity,
        discount_amount: discountAmount, gst_amount: 0,
        total_amount: Math.max(grandTotal - discountAmount, 0),
        profile_items: profileItems,
      });
      setStep(3);
    } catch {
      toast.error("Failed to calculate fees");
    } finally {
      setLoading(false);
    }
  };

  const initiatePayment = async () => {
    setLoading(true);
    try {
      const primaryType = selectedProfiles.length === 1 ? selectedProfiles[0][1].service_type : "both";
      const createResponse = await authFetch(`${API}/payments/create`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: totalFee.total_amount,
          service_type: primaryType,
          questionnaire: {},
          financial_year: selectedFY,
          discount_code: appliedDiscount?.code || "",
          discount_amount: totalFee.discount_amount || 0,
          profile_items: totalFee.profile_items || [],
        }),
      });
      if (!createResponse.ok) throw new Error("Failed to create payment order");
      const paymentData = await createResponse.json();

      // Zero-amount payment (fully discounted) — already completed on backend
      if (paymentData.zero_amount || paymentData.amount_paise === 0) {
        setPaymentStatus("success");
        setStep(4);
        toast.success("Service activated with full discount!");
        return;
      }

      const options = {
        key: paymentData.razorpay_key_id,
        amount: paymentData.amount_paise,
        currency: "INR",
        name: "CA Ronak Patel, Chartered Accountant",
        description: `Services — FY ${selectedFY}${appliedDiscount ? ` (${appliedDiscount.code})` : ""}`,
        order_id: paymentData.razorpay_order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await authFetch(`${API}/payments/verify`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                payment_id: paymentData.payment_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            if (verifyResponse.ok) {
              setPaymentStatus("success");
              setStep(4);
              toast.success("Payment successful!");
            } else {
              toast.error("Payment verification failed");
            }
          } catch { toast.error("Payment verification error"); }
        },
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#145886" },
        modal: { ondismiss: () => { setLoading(false); toast.info("Payment cancelled"); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch {
      toast.error("Payment initiation failed");
    } finally {
      setLoading(false);
    }
  };

  // ===================== RENDER STEPS =====================

  const renderStep1 = () => (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="max-w-2xl mx-auto">
      {/* FY Selector */}
      <div className="soft-card mb-3" data-testid="fy-selector-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-[var(--primary)]" />
            <span className="font-semibold text-[var(--text-main)]">Financial Year</span>
          </div>
          <Select value={selectedFY} onValueChange={(fy) => setSearchParams({ fy })}>
            <SelectTrigger className="w-[180px] border-[var(--border-light)] rounded-xl" data-testid="fy-dropdown">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fyOptions.map((fy) => <SelectItem key={fy} value={fy} data-testid={`fy-option-${fy}`}>FY {fy}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-2 font-medium">Selecting service packages for FY {selectedFY}</p>
      </div>

      {/* Profile-based selection */}
      {hasMultipleProfiles && (
        <div className="mb-4 flex items-center gap-2 text-sm text-[var(--text-muted)]">
          <Users className="w-4 h-4" />
          <span>You have <strong>{profiles.length} profiles</strong>. Select a package for each.</span>
        </div>
      )}

      <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[var(--text-main)] mb-3">Select Packages</h2>

      {Object.entries(profileSelections).map(([pid, sel]) => {
        const profile = pid === "__self__" ? { full_name: user?.name || "Self" } : profiles.find((p) => p.profile_id === pid);
        const profileName = profile?.full_name || pid;
        return (
          <div key={pid} className="mb-3 soft-card !p-5" data-testid={`profile-section-${pid}`}>
            {(hasMultipleProfiles || pid !== "__self__") && (
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-[var(--border-light)]">
                <Checkbox checked={sel.selected} onCheckedChange={(v) => updateSelection(pid, "selected", v)}
                  data-testid={`profile-toggle-${pid}`} />
                <span className="font-bold text-lg text-[var(--text-main)]">{profileName}</span>
                {profile?.pan_number && <span className="text-xs font-bold text-[var(--text-muted)] bg-[var(--background-primary)] border border-[var(--border-light)] px-3 py-1 rounded-full shadow-sm">PAN: {profile.pan_number}</span>}
              </div>
            )}
            {sel.selected && (
              <div className="grid gap-3 pt-2">
                {SERVICE_OPTIONS.map((svc) => (
                  <div key={svc.id} onClick={() => updateSelection(pid, "service_type", svc.id)}
                    className={`p-4 rounded-[20px] cursor-pointer transition-all border ${sel.service_type === svc.id ? "border-[var(--primary)] bg-[var(--primary)]/5 shadow-md transform -translate-y-0.5" : "border-[var(--border-light)] bg-white hover:border-[var(--primary)] hover:shadow-md hover:-translate-y-0.5"}`}
                    data-testid={`service-${svc.id}-${pid}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm border border-[var(--border-light)] ${sel.service_type === svc.id ? "bg-[var(--primary)] border-[var(--primary)]" : "bg-white"}`}>
                        <svc.icon className={`w-6 h-6 ${sel.service_type === svc.id ? "text-white" : "text-[var(--primary)]"}`} />
                      </div>
                      <div className="flex-1 flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                        <div>
                          <h3 className={`font-bold text-base ${sel.service_type === svc.id ? "text-[var(--primary)]" : "text-[var(--text-main)]"}`}>{svc.title}</h3>
                          <p className="text-sm font-medium text-[var(--text-muted)]">{svc.desc}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[var(--success)] font-bold text-base">From {"\u20B9"}{svc.basePrice}</span>
                          {sel.service_type === svc.id && (
                            <div className="w-6 h-6 rounded-full bg-[var(--primary)] flex items-center justify-center shadow-sm">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex justify-end mt-3">
        <button onClick={() => setStep(2)} disabled={selectedProfiles.length === 0}
          className="soft-btn disabled:opacity-50 disabled:cursor-not-allowed" data-testid="next-step-btn">
          Continue <ArrowRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="max-w-2xl mx-auto">
      <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[var(--text-main)] mb-3">Requirements Details</h2>

      {selectedProfiles.map(([pid, sel]) => {
        const profileName = pid === "__self__" ? (user?.name || "Self") : (profiles.find((p) => p.profile_id === pid)?.full_name || pid);
        const q = sel.questionnaire;
        return (
          <div key={pid} className="soft-card mb-3 !p-6" data-testid={`questionnaire-${pid}`}>
            {selectedProfiles.length > 1 && (
              <h3 className="font-bold text-lg text-[var(--primary)] mb-4 pb-3 border-b border-[var(--border-light)]">{profileName} <span className="text-[var(--text-muted)] font-medium text-sm ml-2">— {SERVICE_OPTIONS.find((s) => s.id === sel.service_type)?.title}</span></h3>
            )}
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-bold text-[var(--text-main)] mb-3 block">Income Sources</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {INCOME_SOURCES.map((src) => (
                    <div key={src.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${q.income_sources.includes(src.id) ? "border-[var(--primary)] bg-[var(--primary)]/5 shadow-sm" : "border-[var(--border-light)] bg-white hover:border-[var(--primary)]/50"}`}
                      onClick={() => toggleIncomeSource(pid, src.id)}>
                      <Checkbox checked={q.income_sources.includes(src.id)} onCheckedChange={() => toggleIncomeSource(pid, src.id)}
                        className="h-5 w-5 rounded data-[state=checked]:bg-[var(--primary)] data-[state=checked]:border-[var(--primary)]" />
                      <span className="text-sm font-medium text-[var(--text-main)]">{src.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm font-bold text-[var(--text-main)] mb-3 block">Annual Turnover / Income</Label>
                <Select value={q.annual_turnover} onValueChange={(v) => updateQuestionnaire(pid, "annual_turnover", v)}>
                  <SelectTrigger className="rounded-xl border-[var(--border-light)]"><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="<10L">Below 10 Lakhs</SelectItem>
                    <SelectItem value="10L-50L">10 Lakhs - 50 Lakhs</SelectItem>
                    <SelectItem value="50L-2Cr">50 Lakhs - 2 Crore</SelectItem>
                    <SelectItem value=">2Cr">Above 2 Crore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {sel.service_type !== "itr_filing" && (
                <>
                  <div>
                    <Label className="text-sm font-bold text-[var(--text-main)] mb-3 block">Business Type</Label>
                    <Select value={q.business_type} onValueChange={(v) => updateQuestionnaire(pid, "business_type", v)}>
                      <SelectTrigger className="rounded-xl border-[var(--border-light)]"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual / Sole Proprietor</SelectItem>
                        <SelectItem value="partnership">Partnership Firm</SelectItem>
                        <SelectItem value="llp">LLP</SelectItem>
                        <SelectItem value="company">Private Limited Company</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-[var(--text-main)] mb-3 block">Monthly Transaction Volume</Label>
                    <Select value={q.transaction_volume} onValueChange={(v) => updateQuestionnaire(pid, "transaction_volume", v)}>
                      <SelectTrigger className="rounded-xl border-[var(--border-light)]"><SelectValue placeholder="Select range" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="<100">Below 100</SelectItem>
                        <SelectItem value="100-500">100 - 500</SelectItem>
                        <SelectItem value="500-2000">500 - 2,000</SelectItem>
                        <SelectItem value=">2000">Above 2,000</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div className="flex gap-4 flex-wrap">
                <div className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${q.gst_registered ? "border-[var(--primary)] bg-[var(--primary)]/5 shadow-sm" : "border-[var(--border-light)] bg-white hover:border-[var(--primary)]/50"}`}
                  onClick={() => updateQuestionnaire(pid, "gst_registered", !q.gst_registered)}>
                  <Checkbox checked={q.gst_registered} onCheckedChange={(v) => updateQuestionnaire(pid, "gst_registered", v)}
                    className="h-5 w-5 rounded data-[state=checked]:bg-[var(--primary)] data-[state=checked]:border-[var(--primary)]" />
                  <span className="text-sm font-medium text-[var(--text-main)]">GST Registered</span>
                </div>
                <div className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${q.has_audit_requirement ? "border-[var(--primary)] bg-[var(--primary)]/5 shadow-sm" : "border-[var(--border-light)] bg-white hover:border-[var(--primary)]/50"}`}
                  onClick={() => updateQuestionnaire(pid, "has_audit_requirement", !q.has_audit_requirement)}>
                  <Checkbox checked={q.has_audit_requirement} onCheckedChange={(v) => updateQuestionnaire(pid, "has_audit_requirement", v)}
                    className="h-5 w-5 rounded data-[state=checked]:bg-[var(--primary)] data-[state=checked]:border-[var(--primary)]" />
                  <span className="text-sm font-medium text-[var(--text-main)]">Audit Required</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Discount Code */}
      <div className="soft-card mb-3" data-testid="discount-section">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="w-5 h-5 text-[var(--accent)]" />
          <Label className="text-sm font-bold text-[var(--text-main)]">Discount Code (optional)</Label>
        </div>
        <div className="flex gap-3">
          <Input value={discountCode} onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(""); setAppliedDiscount(null); }}
            placeholder="Enter code" className="rounded-xl flex-1 border-[var(--border-light)]" data-testid="discount-code-input" />
        </div>
        {discountError && <p className="text-xs font-bold text-[var(--error)] mt-2">{discountError}</p>}
        {appliedDiscount && <p className="text-xs font-bold text-[var(--success)] mt-2 flex items-center gap-1.5"><Percent className="w-4 h-4" /> {appliedDiscount.description || appliedDiscount.code} — saves {"\u20B9"}{appliedDiscount.discount_amount?.toLocaleString()}</p>}
      </div>

      <div className="flex justify-between mt-4">
        <button className="soft-btn-outline" onClick={() => setStep(1)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </button>
        <button onClick={calculateAllFees} disabled={loading} className="soft-btn disabled:opacity-50" data-testid="calculate-fee-btn">
          {loading ? "Calculating..." : <><Calculator className="w-4 h-4 mr-2" /> Calculate Fee</>}
        </button>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="max-w-2xl mx-auto">
      <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[var(--text-main)] mb-3">Fee Breakdown — FY {selectedFY}</h2>

      {/* Per-profile breakdown */}
      {totalFee?.profile_items?.length > 1 && (
        <div className="mb-3 space-y-3">
          {totalFee.profile_items.map((item, i) => {
            const sel = profileSelections[item.profile_id];
            return (
              <div key={i} className="flex justify-between items-center p-4 soft-card border border-[var(--border-light)] hover:border-[var(--primary)] transition-all">
                <div>
                  <span className="font-bold text-[var(--text-main)] text-base">{item.profile_name}</span>
                  <span className="text-xs font-medium text-[var(--text-muted)] ml-2">({SERVICE_OPTIONS.find((s) => s.id === item.service_type)?.title})</span>
                </div>
                <span className="font-bold text-[var(--text-main)]">{"\u20B9"}{item.amount?.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="soft-card border border-[var(--border-light)] !p-6 mb-3">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-[var(--text-muted)] font-medium">Base Fee</span>
            <span className="text-[var(--text-main)] font-bold">{"\u20B9"}{totalFee?.base_fee?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[var(--text-muted)] font-medium">Complexity Charges</span>
            <span className="text-[var(--text-main)] font-bold">{"\u20B9"}{totalFee?.complexity_fee?.toLocaleString()}</span>
          </div>
          {totalFee?.discount_amount > 0 && (
            <div className="flex justify-between items-center text-[var(--success)] font-bold bg-[var(--success)]/10 p-3 rounded-xl border border-[var(--success)]/20">
              <span className="flex items-center gap-2"><Tag className="w-4 h-4" /> Discount ({appliedDiscount?.code})</span>
              <span>-{"\u20B9"}{totalFee.discount_amount?.toLocaleString()}</span>
            </div>
          )}
          <div className="border-t border-[var(--border-light)] pt-4 flex justify-between items-center">
            <span className="text-xl font-bold text-[var(--text-main)]">Total Amount</span>
            <span className="text-3xl font-bold text-[var(--success)]">{"\u20B9"}{totalFee?.total_amount?.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-4">
        <button className="soft-btn-outline" onClick={() => setStep(2)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </button>
        <button onClick={initiatePayment} disabled={loading} className="soft-btn !bg-[var(--success)] hover:!bg-[#48a33d] disabled:opacity-50" data-testid="pay-now-btn">
          {loading ? "Processing..." : <><CreditCard className="w-4 h-4 mr-2" /> Pay {"\u20B9"}{totalFee?.total_amount?.toLocaleString()}</>}
        </button>
      </div>
    </motion.div>
  );

  const renderStep4 = () => (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md mx-auto text-center soft-card !p-10 border border-[var(--border-light)]">
      <div className="w-24 h-24 mx-auto mb-3 bg-[var(--success)] rounded-full flex items-center justify-center shadow-lg transform -translate-y-2">
        <CheckCircle className="w-12 h-12 text-white" />
      </div>
      <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[var(--text-main)] mb-4">Payment Successful!</h2>
      <p className="text-[var(--text-muted)] mb-2 font-medium">Thank you for your payment for <strong>FY {selectedFY}</strong>.</p>
      {totalFee?.profile_items?.length > 1 && (
        <p className="text-sm font-bold text-[var(--primary)] mb-4 bg-[var(--primary)]/5 p-2 rounded-lg">{totalFee.profile_items.length} profiles covered in this payment.</p>
      )}
      <p className="text-[var(--text-muted)] mb-3 text-sm leading-relaxed">Your GST-compliant receipt is being generated and will be available in your dashboard shortly.</p>
      <button onClick={() => navigate("/dashboard")} className="soft-btn w-full" data-testid="go-to-dashboard-btn">
        Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
      </button>
    </motion.div>
  );

  return (
    <PortalLayout title="Services">
      <div className="max-w-4xl mx-auto p-6 md:p-8">
        
        {/* Main Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[var(--border-light)] pb-2">
          <button
            className={`pb-2 px-4 font-bold text-lg transition-colors ${mainTab === "my_services" ? "text-[var(--primary)] border-b-2 border-[var(--primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-main)]"}`}
            onClick={() => setMainTab("my_services")}
          >
            My Services
          </button>
          <button
            className={`pb-2 px-4 font-bold text-lg transition-colors ${mainTab === "new_service" ? "text-[var(--primary)] border-b-2 border-[var(--primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-main)]"}`}
            onClick={() => setMainTab("new_service")}
          >
            Buy New Service
          </button>
        </div>

        {mainTab === "my_services" ? (
          <MyServices currentFY={defaultFY} onUpgrade={handleUpgrade} />
        ) : (
          <>
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10 pb-4 border-b border-[var(--border-light)]">
              <div>
                <h2 className="text-2xl font-bold text-[var(--text-main)]">Purchase Service</h2>
                <p className="text-[var(--text-muted)] text-sm">Select a package and answer a few questions to get started.</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-bold text-[var(--primary)] bg-[var(--primary)]/10 px-3 py-1.5 rounded-full border border-[var(--primary)]/20" data-testid="fy-badge">FY {selectedFY}</span>
                <div className="flex flex-col items-end">
                  <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-widest mb-1">Step {step} of 4</span>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4].map((s) => (
                      <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s === step ? "w-8 bg-[var(--primary)] shadow-sm" : s < step ? "w-4 bg-[var(--primary)]/40" : "w-4 bg-[var(--border-light)]"}`} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </>
        )}
      </div>
    </PortalLayout>
  );
};

export default PackageSelection;
